import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { get } from "@/lib/api";
import { PageLoader, PageHeader, StatCard, Badge, statusTone, EmptyState } from "@/components/common";
import { BookOpen, FileText, ClipboardList, PenSquare, FileEdit, Clock, CheckCircle2, XCircle, Activity, AlertTriangle } from "lucide-react";

export default function TeacherDashboard() {
  const nav = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["teacher-dash"], queryFn: () => get("/dashboard/teacher") });
  if (isLoading || !data) return <PageLoader />;
  const s = data.stats;
  const pending = data.profile?.approval_status !== "approved";
  return (
    <div>
      <PageHeader title={`Teacher Studio`} subtitle={`Welcome, ${data.profile?.full_name || "Teacher"}`} />
      {pending && <div className="mb-5 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3 text-amber-800"><AlertTriangle className="h-5 w-5" /><p className="text-sm">Your teacher account is <b>{data.profile?.approval_status}</b>. You can create drafts, but publishing requires admin approval.</p></div>}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard icon={BookOpen} label="Courses" value={s.courses} tone="indigo" />
        <StatCard icon={FileText} label="Mock Tests" value={s.tests} tone="violet" />
        <StatCard icon={PenSquare} label="Questions" value={s.questions} tone="jade" />
        <StatCard icon={ClipboardList} label="PYP" value={s.pyp} tone="amber" />
        <StatCard icon={FileEdit} label="Drafts" value={s.drafts} tone="slate" />
        <StatCard icon={Clock} label="Pending" value={s.pending} tone="amber" />
        <StatCard icon={CheckCircle2} label="Approved" value={s.approved} tone="jade" />
        <StatCard icon={XCircle} label="Rejected" value={s.rejected} tone="crimson" />
      </div>
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3"><h2 className="font-display font-bold text-lg">Recent Approvals</h2><button onClick={() => nav("/teacher/content")} className="text-sm text-indigo-700 font-semibold">View all</button></div>
          {data.approvals.length === 0 ? <EmptyState icon={FileText} title="No submissions yet" /> :
            data.approvals.map((a) => <div key={a.id} className="flex items-center justify-between py-2 border-b border-slate-50"><div><p className="font-medium text-slate-800">{a.title}</p><p className="text-xs text-slate-400">{a.content_type} · {new Date(a.submitted_at).toLocaleDateString()}</p></div><Badge tone={statusTone(a.status)}>{a.status}</Badge></div>)}
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h2 className="font-display font-bold text-lg mb-3">Activity</h2>
          {data.activity.map((a) => <div key={a.id} className="flex items-start gap-2 py-1.5 text-sm text-slate-600"><Activity className="h-4 w-4 text-slate-400 mt-0.5" /><span><b>{a.action}</b> {a.detail}</span></div>)}
        </div>
      </div>
      <div className="flex flex-wrap gap-3 mt-6">
        <button onClick={() => nav("/teacher/questions")} className="bg-indigo-800 text-white font-semibold px-5 py-2.5 rounded-lg">+ Add Question</button>
        <button onClick={() => nav("/teacher/tests")} className="bg-teal-700 text-white font-semibold px-5 py-2.5 rounded-lg">+ Build Test</button>
        <button onClick={() => nav("/teacher/ocr")} className="bg-violet-700 text-white font-semibold px-5 py-2.5 rounded-lg">Import via OCR/AI</button>
      </div>
    </div>
  );
}
